/**
 * ScreenerTab — port of callbacks/screener. Filter form on top, rows below.
 * Hits /api/screener?query=... which already exists in the router.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useStore } from '@/store';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

// MIRRORS THE SERVER. See router/src/core/brokers/terminalTypes.ts.
//
// This declared snake_case fields the API never sends (change_pct,
// market_cap) and an `rsi` field that does not exist server-side. Because
// TypeScript only checks the shape you DECLARE, the compiler was happy and
// every value was undefined at runtime.
interface ScreenerRow {
  symbol:    string;
  name?:     string;
  price:     number;
  change?:   number | null;
  changePct: number | null;
  volume:    number | null;
  avgVolume?: number | null;
  marketCap: number | null;
  pe:        number | null;
  score:     number | null;
  signals?:  string[];
  sector:    string | null;
}

export function ScreenerTab() {
  const [query, setQuery] = useState('');
  const [running, setRunning] = useState(false);
  const setFocus = useStore(s => s.setFocus);

  const { data, isLoading, isError, refetch } = useQuery<ScreenerRow[]>({
    queryKey: ['screener', query],
    queryFn:  () => api(`/api/screener?query=${encodeURIComponent(query)}`),
    enabled:  running,
    staleTime: 30_000,
  });

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input
          placeholder="e.g. mega cap tech with RSI < 30"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setRunning(true); refetch(); } }}
          style={{ flex: 1, fontSize: 11, padding: '4px 8px', height: 24 }}
        />
        <button onClick={() => { setRunning(true); refetch(); }} style={{
          background: ORG, color: '#000', border: 'none',
          fontFamily: FONT, fontSize: 10, fontWeight: 'bold',
          padding: '4px 14px', borderRadius: 2,
        }}>RUN</button>
      </div>

      {isLoading && <div style={{ color: MUT, fontSize: 11 }}>Running screen...</div>}
      {isError && <div style={{ color: RED, fontSize: 11 }}>Screener unavailable</div>}
      {data && data.length === 0 && <div style={{ color: MUT, fontSize: 11 }}>No matches.</div>}

      {data && data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: ORG, fontSize: 9, letterSpacing: 1 }}>
              <th style={th}>Symbol</th>
              <th style={th}>Price</th>
              <th style={th}>%Chg</th>
              <th style={th}>Mkt Cap</th>
              <th style={th}>P/E</th>
              {/* This column renders r.score (a 0-100 composite screen score),
                  NOT RSI -- the API returns no rsi field. Labelling it "RSI"
                  read as an overbought/oversold gauge it isn't. */}
              <th style={th}>SCORE</th>
              <th style={th}>Volume</th>
              <th style={th}>Sector</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr
                key={r.symbol}
                onClick={() => setFocus(r.symbol)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ ...td, color: ORG, fontWeight: 'bold' }}>{r.symbol}</td>
                {/* FIELD NAMES + NULL SAFETY.
                    The API returns camelCase (changePct, marketCap) and this
                    read snake_case, so every value was undefined; calling
                    .toFixed() on undefined threw during render, and with no
                    ErrorBoundary in the tree that white-screened the entire
                    terminal until a manual reload. `rsi` is not returned at
                    all. Fields the API cannot source are now null and render
                    as an em dash rather than a confident zero. */}
                <td style={td}>{num(r.price, 2, '$')}</td>
                <td style={{ ...td, color: Number.isFinite(r.changePct as number) ? ((r.changePct as number) >= 0 ? GRN : RED) : MUT }}>
                  {r.changePct == null ? '—'
                    : `${r.changePct >= 0 ? '+' : ''}${r.changePct.toFixed(2)}%`}
                </td>
                <td style={td}>{r.marketCap == null ? '—' : `$${(r.marketCap / 1e9).toFixed(2)}B`}</td>
                <td style={td}>{r.pe == null ? '—' : r.pe.toFixed(1)}</td>
                <td style={td}>{r.score == null ? '—' : r.score.toFixed(1)}</td>
                <td style={td}>{r.volume == null ? '—' : `${(r.volume / 1e6).toFixed(2)}M`}</td>
                <td style={{ ...td, color: MUT, textAlign: 'left' }}>{r.sector ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Render a possibly-null number without ever throwing.
function num(v: number | null | undefined, dp: number, prefix = ''): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${prefix}${v.toFixed(dp)}` : '—';
}

const th: React.CSSProperties = { textAlign: 'right', padding: '3px 6px', borderBottom: '1px solid #222' };
const td: React.CSSProperties = { textAlign: 'right', padding: '3px 6px', borderBottom: '1px solid #161616', color: '#e0e0e0' };
